'use strict';

import React from 'external/react';
import PropTypes from 'external/prop-types';
import { Button } from 'external/react-bootstrap';

import assetLink from '../utils/assetLink';
import ImageDisplay from './ImageDisplay';
import { default as Icon } from './Icon';
import { AppContext, DataLinkContainer, DataUtil, FaIcon } from 'plugin/liferay-widgets-react-web';

import { getAssetLinkStore } from '../stores/StoreFactory';

const RENDER_STYLE = {
    FOLDER_TREE: 'folderTree',
    FLAT: 'flat',
};

// eslint-disable-next-line react/prop-types
const AttachedListItemImage = ({ item, children }) => {
    return (
        <div className="attached-file-item__image">
            {children}
        </div>
    );
};

// eslint-disable-next-line react/prop-types
const AttachedListItemFileImage = ({
    item,
    defaultPreviewUrl = '/o/fliwas-theme/images/file_system/large/document.png',
    defaultThumbnailUrl = '/o/fliwas-theme/images/file_system/large/document.png'
}) => {
    return (
        <ImageDisplay
            thumbnailUrl={item.thumbnailUrl || defaultThumbnailUrl}
            previewUrl={item.previewUrl || defaultPreviewUrl}
            className="preview-image img-polaroid"
        />
    );
};

// eslint-disable-next-line react/prop-types
const AttachedListItemFolderImage = ({ onFolderClick }) => {
    return (
        <ImageDisplay
            thumbnailUrl={'/o/fliwas-theme/images/file_system/large/folder_full_document.png'}
            className="preview-image img-polaroid"
            onClick={onFolderClick}
        />
    );
};

// eslint-disable-next-line react/prop-types
const AttachedListItemDetails = ({ title, onClick }) => {
    return (
        <div className="attached-file-item__details" onClick={onClick}>
            <span className="title">{title}</span>
        </div>
    );
};

// eslint-disable-next-line react/prop-types
const AttachedListItemButtons = ({ item, onDeleteAttachedAsset }) => {
    return (
        <div className="button-box-nopadding">
            <Button variant="default"
                    title="Öffnen"
                    onClick={() => assetLink.openDocumentDetailsDialog(item.id, item.groupId)}>
                Öffnen
            </Button>

            {onDeleteAttachedAsset &&
                <Button variant="primary" onClick={() => onDeleteAttachedAsset(item)}>
                    <Icon name="x"/>Trennen
                </Button>
            }

            <Button
                variant="outline-primary"
                className="ml-2"
                href={item.downloadUrl + '&download=true'}
                title="Herunterladen"
                download
            >
                <Icon name="download" title="Herunterladen"/>
            </Button>
        </div>
    );
};

// eslint-disable-next-line react/prop-types
const AttachedFileListItem = ({ item, onDeleteAttachedAsset }) => {
    return (
        <div className="attached-file-item">
            <AttachedListItemImage>
                <AttachedListItemFileImage item={item}/>
            </AttachedListItemImage>

            <AttachedListItemDetails title={item.title}/>

            <AttachedListItemButtons item={item} onDeleteAttachedAsset={onDeleteAttachedAsset}/>
        </div>
    );
};

class AttachedFolderListItem extends React.Component {

    constructor(props){
        super(props);
        // eslint-disable-next-line react/prop-types
        this.onFolderClick = props.onFolderClick.bind(this, props.title);
    }

    render(){
        const { title } = this.props;
        return (
            <div className="attached-file-item">
                <AttachedListItemImage>
                    <AttachedListItemFolderImage onFolderClick={this.onFolderClick}/>
                </AttachedListItemImage>

                <AttachedListItemDetails
                    title={title.toLowerCase() === 'topimage' ? 'Titelbild' : title}
                    onClick={this.onFolderClick}
                />
            </div>
        );
    }
}

class FolderNavTitle extends React.Component {
    constructor(props){
        super(props);
        // eslint-disable-next-line react/prop-types
        this.onFolderClick = props.onFolderClick.bind(this, undefined);
    }

    render(){
        // eslint-disable-next-line react/prop-types
        const { currentFolderName, folderFiles, onFolderFilesDownload } = this.props;
        return (
            <h3 className="">
                {currentFolderName.toLowerCase() === 'topimage' ? 'Titelbild' : currentFolderName}
                {
                    onFolderFilesDownload &&
                    <Button
                        variant="outline-primary"
                        className="ml-3"
                        onClick={onFolderFilesDownload.bind(this, currentFolderName, folderFiles)}
                        title={'Alles herunterladen'}
                    >
                        <Icon name={'download'} title={'Alles herunterladen'}/>
                    </Button>
                }
                <Button
                    variant="primary"
                    className="pull-right"
                    onClick={this.onFolderClick}
                    title={'Ordner hoch'}
                >
                    <FaIcon name={'level-up'}/>
                    <span className="ml-2">Ordner hoch</span>
                </Button>
            </h3>
        );
    }
}

// eslint-disable-next-line react/prop-types
const AttachedDocList = ({
    list,
    currentFolderName,
    onFolderClick,
    onFolderFilesDownload,
    renderStyle,
    ...otherProps
}) => {
    let items = DataUtil.asArray(list);
    if (!items || !items.length) {
        return (
            <div className="error-message">Keine verbundenen Dokumente!</div>
        );
    }

    if (renderStyle === RENDER_STYLE.FOLDER_TREE) {
        if (currentFolderName) {
            //filter all files with tag, that is the currentFolder
            const files = items.slice().filter(item => {
                return item.tags && item.tags.find(tag => tag.name === currentFolderName);
            }).sort(function (a, b){
                return a.title.localeCompare(b.title);
            });
            return (
                <React.Fragment>
                    <FolderNavTitle
                        currentFolderName={currentFolderName}
                        onFolderClick={onFolderClick}
                        onFolderFilesDownload={onFolderFilesDownload}
                        folderFiles={files}
                    />
                    <div className="attached-file-list flex-box">
                        {files.map((item, i) =>
                            <AttachedFileListItem {...otherProps} key={'file' + item.id + '_' + i} item={item}/>
                        )}
                    </div>
                </React.Fragment>
            );
        } else {
            //filter all tags and put them (as virtual folderNames) in an object (as keys) named folderNames
            let folderNames = items.reduce((folderNames, file) => {
                file.tags && file.tags.forEach(tag => folderNames[tag.name] = tag.name);
                return folderNames;
            }, {});
            folderNames = Object.keys(folderNames).reduce((result, file) => {
                result.push(file);
                return result;
            }, []).sort(function (a, b){
                return a.localeCompare(b);
            });

            //filter all files without tags, therefore are the folders, in which the virtually lie
            const files = items.slice().filter(item => {
                return !item.tags || item.tags && item.tags.length === 0;
            }).sort(function (a, b){
                return a.title.localeCompare(b.title);
            });
            return (
                <div className="attached-file-list flex-box">
                    {folderNames && folderNames.length > 0 && folderNames.map((folderName, i) =>
                        <AttachedFolderListItem {...otherProps} key={'folderName' + folderName + i} title={folderName}
                                                onFolderClick={onFolderClick}/>
                    )}

                    {files.map((item, i) =>
                        <AttachedFileListItem {...otherProps} key={'file-' + item.id + '_' + i} item={item}/>
                    )}
                </div>
            );
        }
    }

    //RENDER_STYLE = FLAT
    return (
        <React.Fragment>
            {items.map((item, i) =>
                <AttachedFileListItem {...otherProps} key={'file-' + item.id + '_' + i} item={item}/>
            )}
        </React.Fragment>
    );
};

const LinkedAttachedDocList = DataLinkContainer.link(AttachedDocList, 'list', getAssetLinkStore(AppContext));

class AttachedDocumentList extends React.Component {

    constructor(props){
        super(props);
        this.onFolderClick = this.onFolderClick.bind(this);
        this.onFolderFilesDownload = this.onFolderFilesDownload.bind(this);
        this.state = {
            currentFolderName: undefined
        };
    }

    onFolderClick(title){
        this.setState({
            currentFolderName: title
        });
    }

    onFolderFilesDownload(folderTag, folderFiles){
        let url = '/o/multiDocumentDownload?p_auth=' + Liferay.authToken;
        if (folderFiles && folderFiles.length > 0) {
            const fileList = folderFiles.reduce((list, file) => {
                list.push(file.id);
                return list;
            }, []);
            url += '&fileIds=' + fileList.join(',');
        }
        url += '&folderTag=' + folderTag;

        const HWRMwindow = window.open(window.location.protocol + '//' + window.location.host + url, '_blank');
        HWRMwindow.focus();
    }

    render(){
        const { list, moreAttachedEntities, renderStyle = RENDER_STYLE.FOLDER_TREE, ...otherProps } = this.props;
        let List = AttachedDocList;

        if (typeof list === 'string') {
            List = LinkedAttachedDocList;
        }

        return (
            <List {...otherProps}
                  list={list}
                  renderStyle={renderStyle}
                  ListItem={AttachedFileListItem}
                  currentFolderName={this.state.currentFolderName}
                  onFolderClick={this.onFolderClick}
                  onFolderFilesDownload={this.onFolderFilesDownload}
                  className="feed-activity-list modal-list"
                  onClickMore={moreAttachedEntities}/>
        );
    }
}

AttachedDocumentList.propTypes = {
    list: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.array]).isRequired,
    moreAttachedEntities: PropTypes.func,
    renderStyle: PropTypes.string
};

AttachedDocumentList.RENDER_STYLE = RENDER_STYLE;

export default AttachedDocumentList;



// WEBPACK FOOTER //
// ./js/components/AttachedDocumentList.jsx